/**
 * Chart Context Minifier
 *
 * Minifies Highcharts options by removing UI-only properties
 * while preserving the original JSON shape and all data-relevant fields.
 */

import {
  CHART_CONTEXT_LIMITS,
  PROPERTIES_TO_REMOVE,
  SERIES_DATA_POINT_KEEP_PROPERTIES,
  SERIES_PROPERTIES_TO_REMOVE,
} from './constants';
import type { MinifiedChartOptions, ChartEntry } from './types';

/**
 * Deep clone an object
 */
function deepClone<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

/**
 * Get a nested property from an object using dot notation
 */
function getNestedProperty(obj: Record<string, unknown>, path: string): unknown {
  const parts = path.split('.');
  let current: unknown = obj;
  for (const part of parts) {
    if (current === null || current === undefined || typeof current !== 'object') {
      return undefined;
    }
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}

/**
 * Delete a nested property from an object using dot notation
 */
function deleteNestedProperty(obj: Record<string, unknown>, path: string): void {
  const parts = path.split('.');
  if (parts.length === 1) {
    delete obj[parts[0]];
    return;
  }

  let current: unknown = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    if (current === null || current === undefined || typeof current !== 'object') {
      return;
    }
    current = (current as Record<string, unknown>)[parts[i]];
  }

  if (current !== null && current !== undefined && typeof current === 'object') {
    delete (current as Record<string, unknown>)[parts[parts.length - 1]];
  }
}

/**
 * Check if a value is a non-null object
 */
function isObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Remove specified properties from an object, handling arrays of objects
 * (e.g., xAxis can be an object or array of objects)
 */
function removeProperties(obj: Record<string, unknown>, properties: string[]): void {
  for (const prop of properties) {
    const parts = prop.split('.');
    const rootKey = parts[0];
    const restPath = parts.slice(1).join('.');

    // Check if root key exists
    const rootValue = obj[rootKey];
    if (rootValue === undefined) continue;

    if (parts.length === 1) {
      // Direct property - delete it
      delete obj[rootKey];
    } else if (Array.isArray(rootValue)) {
      // Handle arrays (e.g., xAxis, yAxis can be arrays)
      for (const item of rootValue) {
        if (isObject(item)) {
          deleteNestedProperty(item, restPath);
        }
      }
    } else if (isObject(rootValue)) {
      // Handle nested object
      deleteNestedProperty(obj, prop);
    }
  }
}

/**
 * Minify a data point by keeping only essential properties
 */
function minifyDataPoint(dataPoint: unknown): unknown {
  // Primitive values - keep as is
  if (typeof dataPoint !== 'object' || dataPoint === null) {
    return dataPoint;
  }

  // Array format [x, y] - keep as is
  if (Array.isArray(dataPoint)) {
    return dataPoint;
  }

  // Object format - keep only essential properties
  const original = dataPoint as Record<string, unknown>;
  const minified: Record<string, unknown> = {};

  for (const key of SERIES_DATA_POINT_KEEP_PROPERTIES) {
    if (key in original && original[key] !== null && original[key] !== undefined) {
      // For meta object, keep it but potentially simplify
      if (key === 'meta' && isObject(original[key])) {
        const meta = original[key] as Record<string, unknown>;
        // Keep useful meta properties, skip timestamp
        const minifiedMeta: Record<string, unknown> = {};
        if (meta.isoDate !== undefined) minifiedMeta.isoDate = meta.isoDate;
        if (meta.weekLabel !== undefined) minifiedMeta.weekLabel = meta.weekLabel;
        if (meta.year !== undefined) minifiedMeta.year = meta.year;
        if (meta.week !== undefined) minifiedMeta.week = meta.week;
        if (Object.keys(minifiedMeta).length > 0) {
          minified.meta = minifiedMeta;
        }
      } else {
        minified[key] = original[key];
      }
    }
  }

  return minified;
}

/**
 * Check if a data point should be kept (has valid data)
 */
function isValidDataPoint(dataPoint: unknown): boolean {
  if (dataPoint === null || dataPoint === undefined) {
    return false;
  }

  // Primitive number
  if (typeof dataPoint === 'number') {
    return !isNaN(dataPoint);
  }

  // Array format [x, y]
  if (Array.isArray(dataPoint)) {
    return dataPoint.length >= 2 && dataPoint[1] !== null && dataPoint[1] !== undefined;
  }

  // Object format
  if (typeof dataPoint === 'object') {
    const obj = dataPoint as Record<string, unknown>;
    // Must have y value that's not null
    return obj.y !== null && obj.y !== undefined;
  }

  return false;
}

/**
 * Minify a series by removing UI properties and filtering/simplifying data points
 */
function minifySeries(
  series: Record<string, unknown>,
  truncate: boolean = false
): { minified: Record<string, unknown>; removed: number } {
  const minified = deepClone(series);
  let removed = 0;

  // Remove UI-only properties from series
  for (const prop of SERIES_PROPERTIES_TO_REMOVE) {
    delete minified[prop];
  }

  // Process data array
  if (Array.isArray(minified.data)) {
    const originalData = minified.data as unknown[];

    // Filter out null/invalid data points
    let validData = originalData.filter(isValidDataPoint);
    removed = originalData.length - validData.length;

    // Minify each data point
    validData = validData.map(minifyDataPoint);

    // Truncate if needed and still too large
    if (truncate && validData.length > CHART_CONTEXT_LIMITS.MAX_DATA_POINTS_PER_SERIES) {
      const maxPoints = CHART_CONTEXT_LIMITS.MAX_DATA_POINTS_PER_SERIES;
      const keepFirst = Math.floor(maxPoints / 2);
      const keepLast = maxPoints - keepFirst;

      validData = [
        ...validData.slice(0, keepFirst),
        { _truncated: true, _skipped: validData.length - maxPoints },
        ...validData.slice(-keepLast),
      ];
      removed += validData.length - maxPoints;
    }

    minified.data = validData;
  }

  return { minified, removed };
}

/**
 * Minify Highcharts options by removing UI-only properties
 * while preserving the data structure.
 *
 * If the original size is below SKIP_MINIFY_THRESHOLD (30KB),
 * the options are passed through without modification.
 *
 * @param options - Highcharts options object
 * @param chartId - Identifier for this chart
 * @returns MinifiedChartOptions with metadata
 */
export function minifyChartOptions(
  options: Record<string, unknown>,
  chartId: string
): ChartEntry {
  const originalSize = JSON.stringify(options).length;

  // Skip minification if size is below threshold (30KB)
  if (originalSize < CHART_CONTEXT_LIMITS.SKIP_MINIFY_THRESHOLD_BYTES) {
    return {
      chartId,
      options: deepClone(options),
      _meta: {
        originalSizeBytes: originalSize,
        minifiedSizeBytes: originalSize,
        dataPointsRemoved: 0,
        wasTruncated: false,
      },
    };
  }

  // Deep clone to avoid mutating original
  const minified = deepClone(options) as Record<string, unknown>;

  // Remove top-level UI properties
  removeProperties(minified, PROPERTIES_TO_REMOVE);

  // Process series
  let totalDataPointsRemoved = 0;
  if (Array.isArray(minified.series)) {
    const currentSize = JSON.stringify(minified).length;
    const shouldTruncate = currentSize > CHART_CONTEXT_LIMITS.TRUNCATE_THRESHOLD_BYTES;

    minified.series = (minified.series as Record<string, unknown>[]).map((s) => {
      const { minified: minifiedSeries, removed } = minifySeries(s, shouldTruncate);
      totalDataPointsRemoved += removed;
      return minifiedSeries;
    });
  }

  // Handle xAxis categories - keep only the category names if they're objects
  if (minified.xAxis) {
    const processAxisCategories = (axis: Record<string, unknown>) => {
      if (Array.isArray(axis.categories)) {
        axis.categories = (axis.categories as unknown[]).map((cat) => {
          if (typeof cat === 'object' && cat !== null) {
            const catObj = cat as Record<string, unknown>;
            // Keep only name/id from category objects
            if (catObj.name !== undefined) {
              return catObj.name;
            }
            if (catObj.id !== undefined) {
              return catObj.id;
            }
          }
          return cat;
        });
      }
    };

    if (Array.isArray(minified.xAxis)) {
      for (const axis of minified.xAxis as Record<string, unknown>[]) {
        processAxisCategories(axis);
      }
    } else if (isObject(minified.xAxis)) {
      processAxisCategories(minified.xAxis);
    }
  }

  // Clean up empty objects
  cleanupEmptyObjects(minified);

  const minifiedSize = JSON.stringify(minified).length;

  return {
    chartId,
    options: minified,
    _meta: {
      originalSizeBytes: originalSize,
      minifiedSizeBytes: minifiedSize,
      dataPointsRemoved: totalDataPointsRemoved,
      wasTruncated: minifiedSize > CHART_CONTEXT_LIMITS.TRUNCATE_THRESHOLD_BYTES,
    },
  };
}

/**
 * Remove empty objects and arrays recursively
 */
function cleanupEmptyObjects(obj: Record<string, unknown>): void {
  for (const key of Object.keys(obj)) {
    const value = obj[key];

    if (Array.isArray(value)) {
      // Clean up arrays of objects
      for (const item of value) {
        if (isObject(item)) {
          cleanupEmptyObjects(item);
        }
      }
    } else if (isObject(value)) {
      cleanupEmptyObjects(value);

      // Remove empty objects
      if (Object.keys(value).length === 0) {
        delete obj[key];
      }
    }

    // Remove empty strings in title
    if (key === 'title' && isObject(value) && value.text === '') {
      delete obj[key];
    }
  }
}

/**
 * Get the byte size of a JSON-stringified object
 */
export function getJsonByteSize(obj: unknown): number {
  return new TextEncoder().encode(JSON.stringify(obj)).length;
}
